import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
  PieChart,
  Pie,
} from "recharts";
import CustomDate from "../../../../components/common/CustomDate";
import {
  useGetTicketClickToExpandQuery,
  useTicketDashboardQuery,
} from "../../../../Services/dashboard/TicketDashboard";
import TableModal from "../../../../components/reusableComponent/TableModal";
import AnalyticsCardHeader from "./AnalyticsCardHeader";

function parseYyyyMmDdToLocalDate(s: string): Date {
  if (!s || s.length < 10) return new Date();
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7)) - 1;
  const d = Number(s.slice(8, 10));
  return new Date(y, m, d);
}

function formatDateToYyyyMmDd(date: Date): string {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

function buildExpandViewDateRange(endDateStr: string) {
  const endDate = parseYyyyMmDdToLocalDate(endDateStr);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - (30 - 1));
  return {
    startDate,
    endDate,
    key: "selection" as const,
  };
}

const ChartTypeToggle = ({
  chartType,
  setChartType,
  buttonStyle,
}: {
  chartType: string;
  setChartType: (type: string) => void;
  buttonStyle: React.CSSProperties;
}) => (
  <div
    style={{
      display: "flex",
      gap: "8px",
      backgroundColor: "#f8fafc",
      padding: "4px",
      borderRadius: "10px",
      border: "1px solid #e2e8f0",
    }}
  >
    {["bar", "pie"].map((type) => (
      <button
        key={type}
        onClick={() => setChartType(type)}
        style={{
          ...buttonStyle,
          backgroundColor: chartType === type ? "#ffffff" : "transparent",
          border:
            chartType === type ? "2px solid #6366f1" : "2px solid transparent",
          color: chartType === type ? "#6366f1" : "#64748b",
          boxShadow:
            chartType === type ? "0 2px 8px rgba(99, 102, 241, 0.2)" : "none",
        }}
      >
        {type === "bar" && (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="12" y1="20" x2="12" y2="10" />
            <line x1="18" y1="20" x2="18" y2="4" />
            <line x1="6" y1="20" x2="6" y2="16" />
          </svg>
        )}
        {type === "pie" && (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
            <path d="M22 12A10 10 0 0 0 12 2v10z" />
          </svg>
        )}
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </button>
    ))}
  </div>
);

interface IPROPS {
  channelWiseData: any;
  startDate?: string;
  endDate?: string;
  teamId?: string | null;
  workflowId?: string | null;
}

const capitalizeAndFormat = (str: string) => {
  if (!str) return "";
  const formatted = str.replace(/_/g, " ");
  return formatted.charAt(0).toUpperCase() + formatted.slice(1).toLowerCase();
};

const COLORS = [
  "#6366f1", "#8b5cf6", "#ec4899", "#f59e0b",
  "#10b981", "#06b6d4", "#f97316", "#ef4444",
];

const ChannelWise: React.FC<IPROPS> = ({ channelWiseData, startDate, endDate, teamId, workflowId }) => {
  const navigate = useNavigate();
  const [fullscreenChart, setFullscreenChart] = useState<any>(null);
  const [modalDateRange, setModalDateRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);
  const [chartType, setChartType] = useState("bar");
  const [activeIndex, setActiveIndex] = useState<number | undefined>();

  useEffect(() => {
    if (fullscreenChart && endDate) {
      setModalDateRange([buildExpandViewDateRange(endDate)]);
    }
  }, [fullscreenChart, endDate]);

  const modalRangeStrings = useMemo(
    () => ({
      start: modalDateRange?.[0]?.startDate
        ? formatDateToYyyyMmDd(modalDateRange[0].startDate)
        : "",
      end: modalDateRange?.[0]?.endDate
        ? formatDateToYyyyMmDd(modalDateRange[0].endDate)
        : "",
    }),
    [modalDateRange],
  );

  const { data: modalOverviewData } = useTicketDashboardQuery(
    {
      dashboardType: "analytics",
      selectedTeam: teamId ?? "",
      selectedWorkFlow: workflowId ?? "",
      graphStartDate: modalRangeStrings.start,
      graphEndDate: modalRangeStrings.end,
    },
    {
      skip:
        !fullscreenChart ||
        !teamId ||
        !modalRangeStrings.start ||
        !modalRangeStrings.end,
    },
  );

  const expandStart = fullscreenChart ? modalRangeStrings.start : startDate ?? "";
  const expandEnd = fullscreenChart ? modalRangeStrings.end : endDate ?? "";

  // ── Click-to-expand state ─────────────────────────────────────────────────
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [clickToExpandPage, setClickToExpandPage] = useState(1);

  const ResponsiveChartContainer = ResponsiveContainer as any;
  const BarChartContainer = BarChart as any;
  const BarContainer = Bar as any;
  const XAxisContainer = XAxis as any;
  const YAxisContainer = YAxis as any;
  const TooltipContainer = Tooltip as any;
  const CartesianGridContainer = CartesianGrid as any;
  const CellContainer = Cell as any;
  const PieChartContainer = PieChart as any;
  const PieContainer = Pie as any;
  const LegendContainer = Legend as any;

  // raw channel name (lowercase, underscored) stored for API value
  const chartData = channelWiseData?.tickets_by_channel
    ? channelWiseData.tickets_by_channel.map((item: any, index: number) => ({
        channel: capitalizeAndFormat(item.channel),
        rawChannel: item.channel, // original for API value
        tickets: item.channel_count,
        color: COLORS[index % COLORS.length],
      }))
    : [];

  const maxValue =
    chartData.length > 0
      ? Math.max(...chartData.map((item: any) => item.tickets))
      : 0;

  const calculateYAxisDomain = (max: number) => {
    if (max <= 100) return [0, 100];
    if (max <= 300) return [0, 300];
    if (max <= 500) return [0, 500];
    if (max <= 1000) return [0, 1000];
    const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
    const step = Math.ceil(max / 10 / magnitude) * magnitude;
    return [0, Math.ceil(max / step) * step];
  };

  const yAxisDomain = calculateYAxisDomain(maxValue);

  const totalTickets = chartData.reduce(
    (sum: number, item: any) => sum + item.tickets,
    0
  );

  const pieData = chartData.map((item: any) => ({
    name: item.channel,
    rawChannel: item.rawChannel,
    value: item.tickets,
    fill: item.color,
  }));

  const modalChartData = useMemo(() => {
    const src =
      fullscreenChart && modalOverviewData != null
        ? modalOverviewData
        : channelWiseData;
    return src?.tickets_by_channel
      ? src.tickets_by_channel.map((item: any, index: number) => ({
          channel: capitalizeAndFormat(item.channel),
          rawChannel: item.channel,
          tickets: item.channel_count,
          color: COLORS[index % COLORS.length],
        }))
      : [];
  }, [fullscreenChart, modalOverviewData, channelWiseData]);

  // ── Click-to-expand query ─────────────────────────────────────────────────
  const clickToExpandParams =
    tableModalOpen && selectedChannel && teamId && expandStart && expandEnd
      ? {
          graph_name: "tickets_by_channel",
          value: selectedChannel,
          start_date: expandStart,
          end_date: expandEnd,
          team_id: teamId,
          page: clickToExpandPage,
          page_size: 10,
          workflow_id: workflowId || null,
        }
      : null;

  const { data: clickToExpandData, isLoading: isClickToExpandLoading } =
    useGetTicketClickToExpandQuery(
      clickToExpandParams ?? {
        graph_name: "",
        value: "",
        start_date: "",
        end_date: "",
        team_id: "",
      },
      { skip: !clickToExpandParams }
    );

  const clickToExpandRows = useMemo(() => {
    if (!clickToExpandData) return [];
    const raw =
      (clickToExpandData as any)?.results ??
      (clickToExpandData as any)?.data ??
      clickToExpandData;
    return Array.isArray(raw) ? raw : [];
  }, [clickToExpandData]);

  const clickToExpandPagination = useMemo(() => {
    const d = clickToExpandData as any;
    if (!d) return null;
    const count = d.count ?? (Array.isArray(d.results) ? d.results.length : 0);
    const totalPages = d.total_pages ?? 1;
    const page = d.page ?? clickToExpandPage;
    const pageSize = d.page_size ?? 15;
    return { totalCount: count, currentPage: page, totalPages, pageSize };
  }, [clickToExpandData, clickToExpandPage]);

  useEffect(() => {
    if (tableModalOpen) setClickToExpandPage(1);
  }, [selectedChannel]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChannelClick = (rawChannel: string) => {
    if (!teamId || !expandStart || !expandEnd) return;
    setSelectedChannel(rawChannel);
    setTableModalOpen(true);
    setClickToExpandPage(1);
  };

  const handleCloseModal = () => {
    setTableModalOpen(false);
    setSelectedChannel(null);
    setClickToExpandPage(1);
  };

  const clickable = !!(teamId && expandStart && expandEnd);

  const CustomLegend = ({ slices }: { slices: typeof pieData }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {slices.map((item, index) => {
        const isActive = activeIndex === index;
        return (
          <div
            key={item.name}
            onMouseEnter={() => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(undefined)}
            onClick={() => handleChannelClick(item.rawChannel)}
            style={{
              padding: "8px 12px",
              borderRadius: 6,
              cursor: clickable ? "pointer" : "default",
              background: isActive ? `${item.fill}10` : "#f8fafc",
              border: `1.5px solid ${isActive ? item.fill : "transparent"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              transition: "all 0.2s ease",
              transform: isActive ? "scale(1.02)" : "scale(1)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: item.fill, boxShadow: isActive ? `0 0 8px ${item.fill}80` : "none" }} />
              <span style={{ color: "#374151", fontWeight: isActive ? "600" : "500", fontSize: "13px" }}>{item.name}</span>
            </div>
            <strong style={{ color: isActive ? item.fill : "#1f2937", fontSize: "14px", minWidth: "30px", textAlign: "right" }}>
              {item.value}
            </strong>
          </div>
        );
      })}
    </div>
  );

  const CustomTooltip = ({
    active,
    payload,
    ticketTotal = totalTickets,
  }: any) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0];
    const value = data.value || data.payload.tickets;
    const denom = ticketTotal > 0 ? ticketTotal : 1;
    const percentage = ((value / denom) * 100).toFixed(1);
    const color = data.payload.fill || data.payload.color;
    const name = data.name || data.payload.channel;
    return (
      <div style={{ background: "white", padding: "8px 12px", borderRadius: 6, boxShadow: "0 2px 8px rgba(0,0,0,0.12)", border: `2px solid ${color}` }}>
        <div style={{ color, fontWeight: "600", fontSize: "13px", marginBottom: 2 }}>{name}</div>
        <div style={{ color: "#6b7280", fontSize: "12px" }}>{value} tickets ({percentage}%)</div>
        {clickable && <div style={{ color: "#94a3b8", fontSize: "11px", marginTop: 4 }}>Click to see details</div>}
      </div>
    );
  };

  const downloadCSV = () => {
    const headers = ["Channel", "Ticket count"];
    const rows = chartData.map((item: any) => [item.channel, item.tickets]);
    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "channel_wise_tickets.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const theme = {
    colors: {
      surface: "#ffffff",
      primary: "#6366F1",
      text: "#1f2937",
      textSecondary: "#4b5563",
      textMuted: "#6b7280",
    },
  };

  const styles = {
    chartTypeButton: {
      padding: "8px 16px",
      border: "2px solid #e2e8f0",
      borderRadius: "8px",
      backgroundColor: "#ffffff",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: 600,
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    expandButton: {
      background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      padding: "12px 20px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: 600,
      transition: "all 0.3s",
      boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
  };

  const modalTicketsTotal = modalChartData.reduce(
    (sum: number, item: any) => sum + item.tickets,
    0,
  );
  const modalTableData = modalChartData.map((item: any) => ({
    channel: item.channel,
    tickets: item.tickets,
    percentage:
      modalTicketsTotal > 0
        ? ((item.tickets / modalTicketsTotal) * 100).toFixed(1) + "%"
        : "0%",
  }));

  const getXAxisProps = (dataLength: number) => ({
    dataKey: "channel",
    tick: { fontSize: 12, fill: "#64748b", fontWeight: 500 },
    tickLine: false,
    axisLine: { stroke: "#cbd5e1", strokeWidth: 2 },
    interval: 0,
    height: dataLength > 4 ? 56 : 48,
    tickMargin: 8,
    label: {
      value: "Channel",
      position: "insideBottom",
      offset: -2,
      style: { fontSize: 13, fill: "#475569", fontWeight: 600 },
    },
  });

  const getChartMargin = (dataLength: number) => ({
    top: 10,
    right: 30,
    left: 10,
    bottom: dataLength > 4 ? 36 : 28,
  });

  const commonAxisProps = {
    yAxis: {
      tick: { fontSize: 12, fill: "#64748b", fontWeight: 500 },
      tickLine: false,
      axisLine: { stroke: "#cbd5e1", strokeWidth: 2 },
      domain: yAxisDomain,
      label: { value: "Ticket count", angle: -90, position: "insideLeft", style: { fontSize: 13, fill: "#475569", fontWeight: 600 } },
    },
  };

  const gridProps = { strokeDasharray: "3 3", stroke: "#e2e8f0", vertical: false };

  const renderChart = (data: any, height: any = "100%") => {
    const commonProps = { margin: getChartMargin(data.length) };
    const ticketSum = data.reduce((s: number, i: any) => s + i.tickets, 0);
    const rowMax =
      data.length > 0 ? Math.max(...data.map((item: any) => item.tickets)) : 0;
    const barYDomain = calculateYAxisDomain(rowMax);
    const yAxisForBars = { ...commonAxisProps.yAxis, domain: barYDomain };

    if (chartType === "bar") {
      return (
        <ResponsiveChartContainer width="100%" height={height}>
          <BarChartContainer data={data} {...commonProps}>
            <CartesianGridContainer {...gridProps} />
            <XAxisContainer {...getXAxisProps(data.length)} />
            <YAxisContainer {...yAxisForBars} />
            <TooltipContainer
              content={(props: any) => (
                <CustomTooltip {...props} ticketTotal={ticketSum} />
              )}
              cursor={{ fill: "rgba(99, 102, 241, 0.08)" }}
            />
            <BarContainer
              dataKey="tickets"
              radius={[8, 8, 0, 0]}
              maxBarSize={60}
              onClick={(entry: any) => handleChannelClick(entry.rawChannel)}
              style={{ cursor: clickable ? "pointer" : "default" }}
            >
              {data.map((entry: any, index: number) => (
                <CellContainer key={`cell-${index}`} fill={entry.color} />
              ))}
            </BarContainer>
          </BarChartContainer>
        </ResponsiveChartContainer>
      );
    }
    const pieSlices = data.map((item: any) => ({
      name: item.channel,
      rawChannel: item.rawChannel,
      value: item.tickets,
      fill: item.color,
    }));
    return (
      <ResponsiveChartContainer width="100%" height={height}>
        <PieChartContainer>
          <PieContainer
            data={pieSlices}
            innerRadius="80%"
            outerRadius="100%"
            cornerRadius="50%"
            paddingAngle={0}
            dataKey="value"
            onMouseEnter={(_: any, index: number) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(undefined)}
            onClick={(_: any, index: number) =>
              handleChannelClick(pieSlices[index]?.rawChannel)
            }
            isAnimationActive={true}
            style={{ cursor: clickable ? "pointer" : "default" }}
          >
            {pieSlices.map((d, i) => (
              <CellContainer
                key={i}
                fill={d.fill}
                strokeWidth={activeIndex === i ? 4 : 2}
                stroke="white"
                style={{
                  filter: activeIndex === i
                    ? `drop-shadow(0 4px 12px ${d.fill}60)`
                    : "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                  transition: "all 0.3s ease",
                }}
              />
            ))}
          </PieContainer>
          <TooltipContainer
            content={(props: any) => (
              <CustomTooltip {...props} ticketTotal={ticketSum} />
            )}
          />
          <LegendContainer
            content={<CustomLegend slices={pieSlices} />}
            layout="vertical"
            align="right"
            verticalAlign="middle"
            wrapperStyle={{ paddingLeft: 20 }}
          />
        </PieChartContainer>
      </ResponsiveChartContainer>
    );
  };

  const FullscreenModal = ({ isOpen, onClose, title }: any) => {
    if (!isOpen) return null;
    const tableKeys =
      modalTableData && modalTableData.length > 0
        ? Object.keys(modalTableData[0])
        : [];

    return (
      <div
        style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(255, 255, 255, 0.75)", zIndex: 9999, padding: "24px", overflow: "auto" }}
        onClick={onClose}
      >
        <div
          style={{ backgroundColor: theme.colors.surface, borderRadius: "20px", width: "95%", maxWidth: "1600px", margin: "0 auto", padding: "32px", position: "relative", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "28px", paddingBottom: "20px", borderBottom: "2px solid #e2e8f0" }}>
            <div>
              <h2 style={{ margin: 0, color: theme.colors.text, fontWeight: 700 }}>{title}</h2>
              <p style={{ margin: "8px 0 0 0", color: theme.colors.textMuted, fontSize: "14px" }}>
                Ticket distribution across {modalChartData.length} channels • Total: {modalTicketsTotal} tickets
              </p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap", flexShrink: 0 }}>
              <div style={{ position: "relative", zIndex: 10002 }}>
                <CustomDate
                  dateRange={modalDateRange}
                  setDateRange={setModalDateRange}
                  unrestricted
                />
              </div>
              <button
                type="button"
                onClick={onClose}
                style={{ background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", color: "white", border: "none", borderRadius: "50%", width: "44px", height: "44px", fontSize: "24px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.3s", boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "rotate(90deg) scale(1.1)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "rotate(0deg) scale(1)"; }}
              >×</button>
            </div>
          </div>
          <div style={{ marginBottom: "32px" }}>
            <div style={{ width: "100%", height: "550px", backgroundColor: "white", padding: "24px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}>
              {renderChart(modalChartData, "100%")}
            </div>
          </div>
          {modalTableData && modalTableData.length > 0 && (
            <div style={{ marginTop: "32px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <h3 style={{ margin: 0, color: theme.colors.text, fontSize: "1.5em", fontWeight: 700 }}>📊 Channel data</h3>
                <span style={{ backgroundColor: theme.colors.primary, color: "white", padding: "8px 16px", borderRadius: "8px", fontSize: "14px", fontWeight: 600 }}>{modalTableData.length} channels</span>
              </div>
              <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: "12px", maxHeight: "450px", overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                  <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                    <tr style={{ backgroundColor: "#f8fafc" }}>
                      {tableKeys.map((key) => (
                        <th key={key} style={{ padding: "14px 18px", textAlign: "left", borderBottom: "2px solid #cbd5e1", fontWeight: 700, color: theme.colors.textSecondary, whiteSpace: "nowrap", position: "sticky", top: 0, backgroundColor: "#f8fafc" }}>
                          {key.toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {modalTableData.map((row: any, index: number) => (
                      <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc", transition: "background-color 0.2s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#eef2ff")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#ffffff" : "#f8fafc")}
                      >
                        {tableKeys.map((key) => (
                          <td key={key} style={{ padding: "14px 18px", borderBottom: "1px solid #e2e8f0", color: theme.colors.textSecondary }}>
                            {row[key] !== null && row[key] !== undefined ? (row[key] === 0 ? "-" : String(row[key])) : "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const hasData = chartData.length > 0;

  return (
    <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)", overflow: "hidden", height: "100%", width: "100%" }}>
      <div style={{ padding: "32px" }}>
        <AnalyticsCardHeader
          icon={
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
            </svg>
          }
          title="Channel wise tickets"
          subtitle={
            <>
              {chartData.length} channels • {totalTickets} tickets
              {clickable && (
                <span style={{ color: "#94a3b8" }}>
                  {" "}
                  • Click to see ticket details
                </span>
              )}
            </>
          }
          actions={
            <>
              <ChartTypeToggle
                chartType={chartType}
                setChartType={setChartType}
                buttonStyle={styles.chartTypeButton}
              />

              <button onClick={downloadCSV}
                style={{ padding: "10px 18px", backgroundColor: "#f8fafc", border: "2px solid #e2e8f0", borderRadius: "10px", cursor: "pointer", fontSize: "14px", fontWeight: 600, color: "#64748b", transition: "all 0.3s", display: "flex", alignItems: "center", gap: "8px" }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#eef2ff"; e.currentTarget.style.borderColor = "#6366f1"; e.currentTarget.style.color = "#6366f1"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#f8fafc"; e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.color = "#64748b"; }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                CSV
              </button>

              <button
                onClick={() => setFullscreenChart({ title: "Channel wise tickets" })}
                style={styles.expandButton}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(99, 102, 241, 0.4)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(99, 102, 241, 0.3)"; }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" /></svg>
                Expand View
              </button>
            </>
          }
        />

        {hasData ? (
          <div style={{ width: "100%", height: "550px", backgroundColor: "#fafbfc", padding: "24px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}>
            {renderChart(chartData, "100%")}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "550px", backgroundColor: "#fafbfc", borderRadius: "16px", border: "1px solid #e2e8f0" }}>
            <div style={{ width: "64px", height: "64px", backgroundColor: "#f3f4f6", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
              <svg style={{ width: "32px", height: "32px", color: "#9ca3af" }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="12" y1="8" x2="12" y2="16" />
              </svg>
            </div>
            <h3 style={{ color: "#64748b", fontSize: "1.25em", fontWeight: 600 }}>No data available</h3>
            <p style={{ color: "#94a3b8", fontSize: "14px", marginTop: "8px" }}>There are no tickets to display in this chart.</p>
          </div>
        )}
      </div>

      <FullscreenModal
        isOpen={!!fullscreenChart}
        onClose={() => setFullscreenChart(null)}
        title={fullscreenChart?.title}
      />

      {/* ── Click-to-expand modal ── */}
      <TableModal
        isOpen={tableModalOpen}
        onRequestClose={handleCloseModal}
        title={selectedChannel ? `Tickets via ${capitalizeAndFormat(selectedChannel)}` : "Tickets"}
        contentLabel="Channel wise tickets click-to-expand"
        emptyMessage="No tickets found for this channel."
        data={clickToExpandRows}
        isLoading={isClickToExpandLoading}
        getRowKey={(row: any, idx: number) => row?.ticket_id ?? row?.id ?? idx}
        maxWidth="1200px"
        onRowClick={(row: any) => {
          const ticketId = row?.ticket_id ?? row?.id;
          if (ticketId != null) {
            handleCloseModal();
            navigate(`/ticket/information/${ticketId}`);
          }
        }}
        totalCount={clickToExpandPagination?.totalCount}
        currentPage={clickToExpandPagination?.currentPage}
        totalPages={clickToExpandPagination?.totalPages}
        pageSize={clickToExpandPagination?.pageSize}
        onPageChange={setClickToExpandPage}
        columns={[
          { label: "Ticket ID", render: (row: any) => row?.ticket_id ?? row?.id ?? "—" },
          { label: "Title", render: (row: any) => row?.title ?? "—" },
          { label: "Contact Name", render: (row: any) => row?.contact_name ?? "—" },
          { label: "Contact Email", render: (row: any) => row?.contact_email ?? "—" },
          { label: "Contact Phone", render: (row: any) => row?.contact_phone ?? "—" },
          {
            label: "Created",
            render: (row: any) => {
              const raw = row?.created;
              if (!raw) return "—";
              const d = new Date(raw);
              if (Number.isNaN(d.getTime())) return "—";
              const dd = String(d.getDate()).padStart(2, "0");
              const mm = String(d.getMonth() + 1).padStart(2, "0");
              const yyyy = d.getFullYear();
              const hh = String(d.getHours()).padStart(2, "0");
              const min = String(d.getMinutes()).padStart(2, "0");
              const ss = String(d.getSeconds()).padStart(2, "0");
              return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
            },
          },
        ]}
      />
    </div>
  );
};

export default ChannelWise;